import React, {ReactNode} from 'react';

import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {UnreachableError} from '../../../libs/shared/core/src/helpers/types/customErrors';
import {isDefined} from '../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {reportToBugsnag} from '../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {IconName} from '../../../libs/shared/ui/src/components/icon/icon';
import {DataFieldType} from '../../models/api/dataFieldsTableColumnModel';
import {dataFieldsTableListValueSeparator} from '../../models/api/dataFieldsTableModel';
import {buildPublicId, ResourceNames, tryParsePublicId} from '../../models/applications/publicIdTypes';

export function deserializeDataFieldsTableResourceValue(
  value: string | undefined,
  resourceType: ResourceNames,
) {
  if (value === undefined) {
    return undefined;
  }

  return tryParsePublicId(value, resourceType);
}

export function deserializeDataFieldsTableResourceListValue(
  value: string | undefined,
  resourceType: ResourceNames,
) {
  if (value === undefined) {
    return undefined;
  }
  return value
    .split(dataFieldsTableListValueSeparator)
    .map((id) => deserializeDataFieldsTableResourceValue(id, resourceType))
    .filter(isDefined);
}

export function serializeDataFieldsTableResourceValue(value: number | null, resourceType: ResourceNames) {
  if (value === null) {
    return undefined;
  }
  return buildPublicId(resourceType, value);
}

export function serializeDataFieldsTableResourceListValue(
  value: ReadonlyArray<number> | null | undefined,
  resourceType: ResourceNames,
) {
  if (!value || value.length === 0) {
    return undefined;
  }
  return value
    .map((id) => serializeDataFieldsTableResourceValue(id, resourceType))
    .join(dataFieldsTableListValueSeparator);
}

export function isValidDataFieldsTableValueForType(type: DataFieldType, value: string | undefined) {
  if (value === undefined) {
    return true;
  }

  switch (type) {
    case DataFieldType.BOOLEAN:
      return ['true', 'false'].includes(value);
    case DataFieldType.NUMBER:
      return value !== '' && isFinite(Number(value));
    // The backend validates that the language value is included in the language param enum.
    case DataFieldType.LANGUAGE:
    case DataFieldType.STRING:
      return true;
    case DataFieldType.CANNED_ANSWER:
      return Boolean(deserializeDataFieldsTableResourceValue(value, 'cannedAnswer'));
    case DataFieldType.INBOX:
      return Boolean(deserializeDataFieldsTableResourceValue(value, 'inbox'));
    case DataFieldType.INBOX_LIST:
      return Boolean(deserializeDataFieldsTableResourceListValue(value, 'inbox')?.length);
    case DataFieldType.TAG:
      return Boolean(deserializeDataFieldsTableResourceValue(value, 'tag'));
    case DataFieldType.TAG_LIST:
      return Boolean(deserializeDataFieldsTableResourceListValue(value, 'tag')?.length);
    case DataFieldType.TEAMMATE:
      return Boolean(deserializeDataFieldsTableResourceValue(value, 'teammate'));
    case DataFieldType.TEAMMATE_LIST:
      return Boolean(deserializeDataFieldsTableResourceListValue(value, 'teammate')?.length);
    default:
      reportToBugsnag(new UnreachableError(type));
      return false;
  }
}

export const dataFieldsTableColumnTypeRenderData: Record<
  DataFieldType,
  {icon: IconName; selectedIcon: IconName; label: ReactNode}
> = {
  [DataFieldType.STRING]: {
    icon: 'contentSmall',
    selectedIcon: 'contentSmall',
    label: <Trans id="rule-editor-data-table-type-dropdown-label-string">Text</Trans>,
  },
  [DataFieldType.BOOLEAN]: {
    icon: 'togglesSmall',
    selectedIcon: 'togglesFilledSmall',
    label: <Trans id="rule-editor-data-table-type-dropdown-label-boolean">Yes or no</Trans>,
  },
  [DataFieldType.NUMBER]: {
    icon: 'numbersSmall',
    selectedIcon: 'numbersSmall',
    label: <Trans id="rule-editor-data-table-type-dropdown-label-number">Number</Trans>,
  },
  [DataFieldType.CANNED_ANSWER]: {
    icon: 'messageTemplateSmall',
    selectedIcon: 'messageTemplateFilledSmall',
    label: <Trans id="rule-editor-data-table-type-dropdown-label-canned-answer">Message template</Trans>,
  },
  [DataFieldType.TAG]: {
    icon: 'tagSmall',
    selectedIcon: 'tagFilledSmall',
    label: <Trans id="rule-editor-data-table-type-dropdown-label-tag">Tag</Trans>,
  },
  [DataFieldType.TAG_LIST]: {
    icon: 'tagsSmall',
    selectedIcon: 'tagsFilledSmall',
    label: <Trans id="rule-editor-data-table-type-dropdown-label-tag-list">Tag list</Trans>,
  },
  [DataFieldType.INBOX]: {
    icon: 'inboxSmall',
    selectedIcon: 'inboxFilledSmall',
    label: <Trans id="rule-editor-data-table-type-dropdown-label-inbox">Inbox</Trans>,
  },
  [DataFieldType.INBOX_LIST]: {
    icon: 'inboxListSmall',
    selectedIcon: 'inboxListFilledSmall',
    label: <Trans id="rule-editor-data-table-type-dropdown-label-inbox-list">Inbox list</Trans>,
  },
  [DataFieldType.TEAMMATE]: {
    icon: 'teammateSmall',
    selectedIcon: 'participantFilledSmall',
    label: <Trans id="rule-editor-data-table-type-dropdown-label-teammate">Teammate</Trans>,
  },
  [DataFieldType.TEAMMATE_LIST]: {
    icon: 'teammatesSmall',
    selectedIcon: 'teammatesFilledSmall',
    label: <Trans id="rule-editor-data-table-type-dropdown-label-teammate-list">Teammate list</Trans>,
  },
  [DataFieldType.LANGUAGE]: {
    icon: 'globeSmall',
    selectedIcon: 'globeSmall',
    label: <Trans id="rule-editor-data-table-type-dropdown-label-language">Language</Trans>,
  },
};
